/**
 * Bobine Client-Side Watermark Engine
 * ====================================
 * Reproduces the server-side watermark (Pillow) in Canvas for turbo upload.
 * - Éclat d'Or (warm tone enhancement)
 * - Bobine reel icon + "BOBINE — le fil de la mémoire" text
 * - Semi-transparent cartouche
 * - Thumbnail generation
 * - HEIC → JPG auto-conversion (iPhone, Samsung HEIF)
 */

/**
 * Détecte si le fichier est au format HEIC/HEIF.
 * Vérifie le MIME type ET l'extension (Samsung peut envoyer type vide).
 */
export function isHeicFile(file) {
  const type = (file.type || '').toLowerCase();
  if (type === 'image/heic' || type === 'image/heif') return true;
  const name = (file.name || '').toLowerCase();
  return name.endsWith('.heic') || name.endsWith('.heif');
}

/**
 * Convertit un fichier HEIC/HEIF en JPG via l'endpoint serveur (pillow_heif).
 * 100x plus rapide que heic2any côté mobile (~500ms vs 30s par photo).
 * Retourne le file converti ou le file d'origine si pas HEIC ou si conversion échoue.
 */
export async function convertHeicIfNeeded(file) {
  if (!isHeicFile(file)) return file;
  const API = `${(process.env.REACT_APP_BACKEND_URL || 'https://bobine.live')}/api`;
  try {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API}/heic/convert`, { method: 'POST', body: formData });
    if (!res.ok) return file; // Fallback (sera classé unsupported plus loin)
    const blob = await res.blob();
    const newName = (file.name || 'photo.heic').replace(/\.(heic|heif)$/i, '.jpg');
    return new File([blob], newName, { type: 'image/jpeg', lastModified: file.lastModified || Date.now() });
  } catch {
    return file;
  }
}

const WATERMARK_TEXTS = {
  fr: 'BOBINE \u2014 le fil de la m\u00e9moire',
  en: 'BOBINE \u2014 the thread of memory',
  es: 'BOBINE \u2014 el hilo de la memoria',
  pt: 'BOBINE \u2014 o fio da mem\u00f3ria',
};
const GOLD_COLOR = 'rgba(212, 175, 55, 0.95)';
const CARTOUCHE_COLOR = 'rgba(0, 0, 0, 0.30)';
const FONT_SIZE = 20;
const FONT_FAMILY = 'Georgia, "Times New Roman", serif';

function getWatermarkText() {
  const lang = (localStorage.getItem('bobine_lang') || 'fr').toLowerCase();
  return WATERMARK_TEXTS[lang] || WATERMARK_TEXTS.fr;
}

// Cache the reel icon
let _reelIconCache = null;
function loadReelIcon() {
  if (_reelIconCache) return Promise.resolve(_reelIconCache);
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => { _reelIconCache = img; resolve(img); };
    img.onerror = () => resolve(null);
    img.src = '/bobine-reel-transparent.png';
  });
}

// Pre-load icon at module init
loadReelIcon();

/**
 * Apply Éclat d'Or warm tone enhancement
 */
function applyEclatDor(ctx, width, height) {
  // Warm overlay (subtle golden tint)
  ctx.globalCompositeOperation = 'multiply';
  ctx.fillStyle = 'rgb(255, 248, 230)';
  ctx.fillRect(0, 0, width, height);
  ctx.globalCompositeOperation = 'source-over';

  // Boost contrast slightly via a subtle S-curve with overlay blend
  ctx.globalCompositeOperation = 'overlay';
  ctx.fillStyle = 'rgba(128, 128, 128, 0.06)';
  ctx.fillRect(0, 0, width, height);
  ctx.globalCompositeOperation = 'source-over';
}

/**
 * Draw the Bobine watermark on a canvas context
 */
function drawWatermark(ctx, width, height, reelIcon) {
  const marginX = 10;
  const marginY = 8;
  const WATERMARK_TEXT = getWatermarkText();

  ctx.font = `${FONT_SIZE}px ${FONT_FAMILY}`;
  const textMetrics = ctx.measureText(WATERMARK_TEXT);
  const textWidth = textMetrics.width;
  const textHeight = FONT_SIZE;

  const iconSize = textHeight + 16;
  const hasIcon = !!reelIcon;
  const iconGap = hasIcon ? 10 : 0;
  const iconWidth = hasIcon ? iconSize : 0;
  const totalWidth = iconWidth + iconGap + textWidth;

  const blockX = width - totalWidth - marginX;
  const textY = height - textHeight - marginY;
  const iconY = textY - (iconSize - textHeight) / 2;

  // Cartouche (semi-transparent background)
  const padH = 4, padV = 3;
  const cartX = blockX - padH;
  const cartY = Math.min(textY, iconY) - padV;
  const cartW = totalWidth + padH * 2;
  const cartH = Math.max(textHeight, iconSize) + padV * 2;
  const radius = 3;

  ctx.fillStyle = CARTOUCHE_COLOR;
  ctx.beginPath();
  ctx.moveTo(cartX + radius, cartY);
  ctx.lineTo(cartX + cartW - radius, cartY);
  ctx.arcTo(cartX + cartW, cartY, cartX + cartW, cartY + radius, radius);
  ctx.lineTo(cartX + cartW, cartY + cartH - radius);
  ctx.arcTo(cartX + cartW, cartY + cartH, cartX + cartW - radius, cartY + cartH, radius);
  ctx.lineTo(cartX + radius, cartY + cartH);
  ctx.arcTo(cartX, cartY + cartH, cartX, cartY + cartH - radius, radius);
  ctx.lineTo(cartX, cartY + radius);
  ctx.arcTo(cartX, cartY, cartX + radius, cartY, radius);
  ctx.closePath();
  ctx.fill();

  // Icon
  if (hasIcon && reelIcon) {
    ctx.globalAlpha = 0.95;
    ctx.drawImage(reelIcon, blockX, iconY, iconSize, iconSize);
    ctx.globalAlpha = 1.0;
  }

  // Text
  ctx.fillStyle = GOLD_COLOR;
  ctx.font = `${FONT_SIZE}px ${FONT_FAMILY}`;
  ctx.textBaseline = 'top';
  ctx.fillText(WATERMARK_TEXT, blockX + iconWidth + iconGap, textY);
}

/**
 * Process a single image file: resize + Éclat d'Or + watermark + thumbnail
 * Returns { watermarked: base64, thumbnail: base64, width, height }
 */
export function processImage(file, maxWidth = 1200, quality = 0.70) {
  return new Promise(async (resolve) => {
    const timeout = setTimeout(() => resolve({ error: 'timeout' }), 15000);

    try {
      const reelIcon = await loadReelIcon();
      const url = URL.createObjectURL(file);
      const img = new Image();

      img.onload = () => {
        clearTimeout(timeout);
        URL.revokeObjectURL(url);

        try {
          // Resize
          let w = img.width, h = img.height;
          if (w > maxWidth || h > maxWidth) {
            const ratio = Math.min(maxWidth / w, maxWidth / h);
            w = Math.round(w * ratio);
            h = Math.round(h * ratio);
          }

          // Main canvas
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');

          // Draw original image
          ctx.drawImage(img, 0, 0, w, h);

          // Apply Éclat d'Or
          applyEclatDor(ctx, w, h);

          // Apply watermark
          drawWatermark(ctx, w, h, reelIcon);

          // Export watermarked
          const watermarkedB64 = canvas.toDataURL('image/jpeg', quality);
          if (!watermarkedB64 || watermarkedB64.length < 100) {
            resolve({ error: 'canvas_export' });
            return;
          }

          // Generate thumbnail (600px max)
          const thumbMax = 600;
          let tw = w, th = h;
          if (tw > thumbMax || th > thumbMax) {
            const ratio = Math.min(thumbMax / tw, thumbMax / th);
            tw = Math.round(tw * ratio);
            th = Math.round(th * ratio);
          }
          const thumbCanvas = document.createElement('canvas');
          thumbCanvas.width = tw;
          thumbCanvas.height = th;
          const tctx = thumbCanvas.getContext('2d');
          tctx.drawImage(canvas, 0, 0, tw, th);
          const thumbnailB64 = thumbCanvas.toDataURL('image/jpeg', 0.65);

          resolve({
            watermarked: watermarkedB64,
            thumbnail: thumbnailB64,
            width: w,
            height: h,
          });
        } catch (e) {
          // canvas operation OOM / drawImage / toDataURL crash
          resolve({ error: 'canvas_crash' });
        }
      };

      img.onerror = () => {
        clearTimeout(timeout);
        URL.revokeObjectURL(url);
        // Format non supporté par le navigateur (HEIC, RAW, fichier corrompu...)
        resolve({ error: 'unsupported_format' });
      };

      img.src = url;
    } catch (e) {
      clearTimeout(timeout);
      resolve({ error: 'init_failed' });
    }
  });
}

/**
 * Extract basic EXIF data from a file using exif-js
 */
export function extractExif(file) {
  return new Promise((resolve) => {
    const timeout = setTimeout(() => resolve({}), 3000);
    try {
      const reader = new FileReader();
      reader.onload = function (e) {
        try {
          // Import EXIF dynamically
          import('exif-js').then(({ default: EXIF }) => {
            const img = new Image();
            img.onload = function () {
              EXIF.getData(img, function () {
                clearTimeout(timeout);
                const result = {};
                const dateStr = EXIF.getTag(this, 'DateTimeOriginal') || EXIF.getTag(this, 'DateTime');
                if (dateStr) {
                  try {
                    // EXIF date format: "2020:06:15 14:30:00"
                    const parts = dateStr.split(' ');
                    const datePart = parts[0].replace(/:/g, '-');
                    const timePart = parts[1] || '00:00:00';
                    const dt = new Date(`${datePart}T${timePart}`);
                    if (!isNaN(dt.getTime())) {
                      result.taken_at = dt.toISOString();
                      result.year = dt.getFullYear();
                      result.month = dt.getMonth() + 1;
                    }
                  } catch (e) { /* ignore */ }
                }
                const make = EXIF.getTag(this, 'Make');
                if (make) result.camera_make = String(make).trim();
                const model = EXIF.getTag(this, 'Model');
                if (model) result.camera_model = String(model).trim();
                resolve(result);
              });
            };
            img.onerror = () => { clearTimeout(timeout); resolve({}); };
            img.src = e.target.result;
          }).catch(() => { clearTimeout(timeout); resolve({}); });
        } catch (err) { clearTimeout(timeout); resolve({}); }
      };
      reader.onerror = () => { clearTimeout(timeout); resolve({}); };
      reader.readAsDataURL(file);
    } catch (e) { clearTimeout(timeout); resolve({}); }
  });
}
